import type { TrackingData } from "../types/tracking";

import { util } from "../helper/util";
import { searchSuggest } from "../suggest";
import { suggestTracking } from "../tracking";
import { ELEMENT_EVENT, FALLBACK_SEARCH_URL, SAN_SEARCH_TYPE, TRACK_LABEL } from "../types/constants";
import { SquirrelURL } from "../helper";
import { TrackingEvent } from "../events";
import { SAN_HEADER } from "../types/tracking/constants";
import { CLASSNAME, DATA, SELECTOR } from "../types/element/constants";

export type DelayedAction = (() => void) | null;

export class SearchForm {
  formField: HTMLFormElement;
  originalSearchTerm: string | null;

  constructor(form: HTMLFormElement, delayedAction: DelayedAction = null) {
    this.formField = form;
    this.originalSearchTerm = "";

    if (Boolean(this.deleteButton).valueOf()) {
      this.toggleButtonVisibility();
      this.searchField.addEventListener(ELEMENT_EVENT.KEY_UP, this.toggleButtonVisibility.bind(this));
      this.formField.addEventListener(ELEMENT_EVENT.RESET, this.resetHandler.bind(this));
    }

    this.formField.addEventListener(
      ELEMENT_EVENT.SUBMIT,
      ((e: SubmitEvent) => {
        e.preventDefault();
        return this.submitSearch();
      }).bind(this),
    );

    this.searchField.addEventListener(
      ELEMENT_EVENT.FOCUS,
      (() => {
        this.clearFormField();
        this.searchField.classList.remove(CLASSNAME.HINT);

        /*                                                         */
        /*         */
        this.searchField.parentNode.parentNode.classList.add(ELEMENT_EVENT.FOCUS);
      }).bind(this),
    );

    if (document.activeElement && document.activeElement === this.searchField) {
      this.clearFormField();
      this.searchField.classList.remove(CLASSNAME.HINT);
    }

    /*                                              */
    /*                                                                  */
    if (typeof delayedAction === "function") {
      delayedAction();
    }

    this.enableSearch();
  }

  get deleteButton(): HTMLDivElement {
    return this.formField.querySelector(SELECTOR.DELETE_BUTTON) as HTMLDivElement;
  }

  get searchBar(): HTMLDivElement {
    return this.formField.querySelector(SELECTOR.SEARCHBAR) as HTMLDivElement;
  }

  get searchField(): HTMLInputElement {
    return this.formField.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
  }

  get submitButton(): HTMLDivElement {
    return this.formField.querySelector(SELECTOR.SUBMIT_BUTTON) as HTMLDivElement;
  }

  get placeholder(): string {
    return this.searchField.placeholder || this.searchField.getAttribute("placeholder") || "";
  }

  get errorString(): string | null {
    return this.searchField.getAttribute(DATA.ERROR);
  }

  get searchTerm(): string {
    return this.searchField.value;
  }

  getSearchField(): HTMLInputElement {
    return this.searchField;
  }

  setValue(val: string) {
    this.searchField.value = val;
    searchSuggest.setOldSearchTerm(val);
  }

  setSearchFieldAttribute(key: string, val: string) {
    this.searchField.setAttribute(key, val);
  }

  isOnDefault(): boolean {
    return this.searchTerm === this.errorString || this.searchTerm === this.placeholder;
  }

  isEmpty(): boolean {
    return window.o_squirrel.util.string.trim(this.searchTerm).length < 1 || this.isOnDefault();
  }

  hideButtons() {
    this.searchBar.classList.remove(CLASSNAME.SEARCHBAR_SHOW_BUTTONS);
  }

  showButtons() {
    this.searchBar.classList.add(CLASSNAME.SEARCHBAR_SHOW_BUTTONS);
  }

  focusSearchField() {
    this.searchField.focus();
  }

  resetHandler() {
    this.hideButtons();
    this.focusSearchField();
  }

  toggleButtonVisibility() {
    if (this.isEmpty()) {
      this.hideButtons();
    } else {
      this.showButtons();
    }
  }

  clearFormField() {
    if (this.isOnDefault()) {
      this.setValue("");
    }
  }

  cleanInputField() {
    this.setValue("");
    window.o_squirrel.searchSuggest.setSendTrackingLabelOnFocus(false);
    this.searchField.focus();
    this.hideButtons();

    TrackingEvent.trackHeader(SAN_HEADER.X);
  }

  fillDefaultText() {
    if (this.searchTerm === "") {
      this.searchField.placeholder = this.placeholder;
      this.searchField.classList.add(CLASSNAME.HINT);
    } else {
      this.searchField.classList.remove(CLASSNAME.HINT);
    }
  }

  enableSearch() {
    this.searchField.removeAttribute("disabled");
    this.submitButton.removeAttribute("disabled");

    this.deleteButton.addEventListener(ELEMENT_EVENT.CLICK, this.cleanInputField.bind(this));
    this.submitButton.addEventListener(ELEMENT_EVENT.CLICK, this.submitSearch.bind(this));
  }

  sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
  }

  async submitSearch() {
    const searchFieldValue = util.string.trim(this.searchField.value);

    /*                                                                                                                     */
    let retries = 0;
    /*                                                                            */
    const loweredSearchTerm = searchFieldValue.toLowerCase();
    while (
      util.string.trim(this.searchField.getAttribute(DATA.TARGET_TERM) + "") !== loweredSearchTerm &&
      retries <= window.o_squirrel.failingRequestRetriesOnSubmit
    ) {
      await this.sleep(50);
      retries++;
    }

    const trackingParameters: TrackingData = {
      [TRACK_LABEL.PAGELOAD_COMPLETE]: "true",
      [TRACK_LABEL.WK_SEARCH_TYPE]: SAN_SEARCH_TYPE.SEARCH,
    };
    let action: string = this.searchField.getAttribute(DATA.TARGET) as string;
    /*                                                            */
    if (retries === window.o_squirrel.failingRequestRetriesOnSubmit + 1 || !action) {
      action = FALLBACK_SEARCH_URL(searchFieldValue, { [TRACK_LABEL.TARGET_URL_FALLBACK]: "retries" });
    }

    /*                                                               */
    if (!this.isEmpty()) {
      const urlObject = new SquirrelURL(action);
      const hashTrackingParameters = urlObject.trackingParameter;

      const isModified: boolean = this.formField.classList.contains(CLASSNAME.TRACK_MODIFIED);
      const isTrackSuggest: boolean = this.formField.classList.contains(CLASSNAME.TRACK_SUGGEST);
      suggestTracking.trackUsage(trackingParameters, hashTrackingParameters, isModified, isTrackSuggest, false, false);

      window.o_squirrel.submitHistory.addSearch(searchFieldValue);
      util.global.setLocation(urlObject.urlWithoutTrackingParameter);
    }
  }
}

export const searchForm = {
  init: (form: HTMLFormElement, delayedAction?: DelayedAction) => new SearchForm(form, delayedAction),
};
